<template>
  <CCard>
    <div
      v-if="isLoading"
      :style="{ height: '20vh' }"
      class="d-flex flex-column justify-content-center align-items-center"
    >
      <CSpinner
        class="custom-spinner"
        color="info"
        grow
      />
    </div>

    <div v-else>
      <CCallout color="info">
        <small class="text-muted">Total Accounts:</small><br>
        <strong class="h4">
          {{ accountsFormattedAndFiltered.length }}
        </strong>
      </CCallout>

      <CDataTable
        small
        border
        sorter
        column-filter
        :items="accountsFormattedAndFiltered"
        :fields="finalTableFields"
        :items-per-page="10"
        :pagination="{ align: 'center' }"
        :sorter-value="{ column: 'registrationDate', asc: false }"
      >
        <template #email-filter>
          <input
            v-model="searchEmail"
            class="form-control form-control-sm"
          >
        </template>

        <template #registrationDate="{ item }">
          <td>
            {{ item.formattedRegistrationDate }}
          </td>
        </template>

        <template #adminApprovalDate="{item}">
          <td>
            {{ item.formattedAdminApprovalDate }}
          </td>
        </template>

        <template #onboardingStatus="{ item }">
          <td>
            <div v-if="!item.editMode">
              <CBadge :color="getOnboardingStatusBadge(item.onboardingStatus)">
                {{ startCase(item.onboardingStatus) }}
              </CBadge>
              <span @click="showOnboardingStatusSelect(item)">
                <CIcon
                  style="cursor: pointer;"
                  class="ml-2 my-2"
                  name="cil-pencil"
                />
              </span>
            </div>
            <div v-if="item.editMode">
              <CSelect
                v-if="type === 'buyer'"
                :value.sync="item.onboardingStatus"
                :options="buyerOnboardingStatusCodes"
                placeholder="Please select"
              />

              <CSelect
                v-if="type === 'seller'"
                :value.sync="item.onboardingStatus"
                :options="sellerOnboardingStatusCodes"
                placeholder="Please select"
              />

              <span @click="updateOnboardingStatus(item)">
                <CIcon
                  style="cursor: pointer;"
                  class="ml-2 my-2 text-success"
                  name="cil-check-circle"
                />
              </span>
              <span @click="cancelUpdate(item)">
                <CIcon
                  style="cursor: pointer;"
                  class="ml-2 my-2 text-danger"
                  name="cil-x-circle"
                />
              </span>
            </div>
          </td>
        </template>

        <template #unmetRequirements="{ item }">
          <td>
            {{ getSafe(() => item.unmetRequirements && item.unmetRequirements.length ? formatUnmetRequirements(item.unmetRequirements) : 'None') }}
          </td>
        </template>

        <template #adminApprovalRequirement="{ item }">
          <td>
            <CBadge
              v-if="item.adminApprovalRequirement === 'Pending'"
              color="warning"
            >
              Pending
            </CBadge>
            <CBadge
              v-else
              color="success"
            >
              Approved
            </CBadge>
          </td>
        </template>

        <template #ringbaAccountId="{item}">
          <td>
            {{ item.ringbaAccountId }}
          </td>
        </template>

        <template #Actions="{ item }">
          <td>
            <CButton
              color="info"
              size="sm"
              :to="`/dashboard/accounts/${type}/${item.id}`"
              target="_blank"
              class="mr-2 mt-2"
            >
              View Account
            </CButton>

            <CDropdown
              v-if="type.toLowerCase() === 'buyer'"
              color="primary" 
              class="mt-2"
              size="sm"
              :add-toggler-classes="isSetPendingItemLoading[item.id] ? 'c-loading-button c-loading-button-loading' : ''"
              :disabled="isSetPendingItemLoading[item.id]"
            >
              <template #toggler-content>
                <CSpinner
                  size="sm"
                  class="c-loading-button-spinner"
                />
                Set Pending Check
              </template>
              <CDropdownItem
                v-for="itemConfig in buyerRegistrationChecklistItemsConfig"
                :key="itemConfig.id"
                @click="setPendingItem(itemConfig, '', item.registrationId, item.id)"
              >
                {{ itemConfig.name }}
              </CDropdownItem>

              <CDropdownItem
                @click="setPendingItem({}, 'Stripe Verification', item.registrationId, item.id)"
              >
                Stripe Verification
              </CDropdownItem>
            </CDropdown>

            <CDropdown
              v-if="type.toLowerCase() === 'seller'"
              color="primary"
              size="sm" 
              class="mt-2"
              :add-toggler-classes="isSetPendingItemLoading[item.id] ? 'c-loading-button c-loading-button-loading' : ''"
              :disabled="isSetPendingItemLoading[item.id]"
            >
              <template #toggler-content>
                <CSpinner
                  size="sm"
                  class="c-loading-button-spinner"
                />
                Set Pending Check
              </template>

              <CDropdownItem
                v-for="itemConfig in sellerRegistrationChecklistItemsConfig"
                :key="itemConfig.id"
                @click="setPendingItem(itemConfig, '', item.registrationId, item.id)"
              >
                {{ itemConfig.name }}
              </CDropdownItem>

              <CDropdownItem
                @click="setPendingItem({}, 'Stripe Verification', item.registrationId, item.id)"
              >
                Stripe Verification
              </CDropdownItem>
            </CDropdown>
          </td>
        </template>
      </CDataTable>
    </div>
  </CCard>
</template>
<script>
import { mapActions, mapState, mapGetters } from 'vuex'
import { startCase } from 'lodash'

import { dateTimeFormat } from '@/utils'
import { COMMON_ACCOUNT_REQUIREMENTS, getUnmetRequirements } from '@/store/modules/accounts.store'

export default {
  name: 'InactiveAccountsList',

  props: {
    type: {
      type: String,
      default: ''
    },

    showFinalApprovalPendingAccounts: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      isLoading: false,
      searchEmail: "",
      isSetPendingItemLoading: {},
      fields: [
        { key: "onboardingStatus", label: "Onboarding Status" },
        { key: "name", label: "Account Name" },
        { key: "registrationDate", label: "Registered On" },
        { key: 'ringbaAccountId', label: 'Ringba Account Id', sorter: false },
        { key: "callTechnology", label: "Technology" },
        { key: "country", label: "Country" },
        { key: "representativeName", label: "Representative" },
        { key: "email", label: "Email" },
        { key: "adminApprovalRequirement", label: "Final Approval", sorter: true },
        { key: "nextPendingItemForFinalApproval", label: "Next Pending Item", sorter: true },
        { key: "Actions", filter: false, hide: this.showFinalApprovalPendingAccounts }
      ],
      COMMON_ACCOUNT_REQUIREMENTS
    }
  },
  computed: {
    ...mapState("accounts", [
      'pendingSellerAccounts',
      'pendingBuyerAccounts',
      'buyerOnboardingStatusCodes',
      'sellerOnboardingStatusCodes'
    ]),

    ...mapGetters("accounts", [
      'pendingSellerAccountsWithNoAdminApproval',
      'pendingBuyerAccountsWithNoAdminApproval'
    ]),

    ...mapState('registrationChecklistItem', [
      'sellerRegistrationChecklistItemsConfig',
      'buyerRegistrationChecklistItemsConfig'
    ]),

    finalTableFields() {
      let fields = [...this.fields]

      if (!this.showFinalApprovalPendingAccounts) {
        fields.splice(-2, 0, { key: "unmetRequirements", label: "Pending Requirements", sorter: false })
        fields.splice(-1, 0, { key: 'adminApprovalDate', label: 'Final Approval Date', sorter: true })
      }
      
      return fields
    },

    accountsList() {
      switch (this.type) {
        case 'buyer':
          return this.showFinalApprovalPendingAccounts ? this.pendingBuyerAccountsWithNoAdminApproval : this.pendingBuyerAccounts

        case 'seller':
          return this.showFinalApprovalPendingAccounts ? this.pendingSellerAccountsWithNoAdminApproval : this.pendingSellerAccounts

        default:
          return []
      }
    },

    accountsFormattedAndFiltered() {
      const accounts = [...this.accountsList]

      return accounts.filter(account => {
        if (!this.searchEmail) {
          return true
        }

        return account.representative.email.toLowerCase().includes(this.searchEmail.toLowerCase().trim())
      }).map(acc => {
        return {
          ...acc,
          editMode: false,
          email: acc.representative.email,
          country: acc.companyAddress.countryCode,
          representativeName: `${acc.representative.firstName} ${acc.representative.lastName}`, 
          formattedRegistrationDate: dateTimeFormat.format(new Date(acc.registrationDate)),
          formattedAdminApprovalDate: !acc.adminApprovalDate || new Date(acc.adminApprovalDate).getFullYear() === 1 ? '' : dateTimeFormat.format(new Date(acc.adminApprovalDate)),
          unmetRequirements: getUnmetRequirements(acc.requirements),
          adminApprovalRequirement: getUnmetRequirements(acc.requirements).includes(COMMON_ACCOUNT_REQUIREMENTS.ADMIN_APPROVAL_REQUIREMENT) ? 'Pending' : 'Approved',
          nextPendingItemForFinalApproval: acc.nextPendingItemForFinalApproval != null ? acc.nextPendingItemForFinalApproval : '',
        }
      })
    }
  },
  async created() {
    try {
      this.isLoading = true
      if (this.type === 'buyer') {
        await this.getPendingBuyerAccounts()
      } else {
        await this.getPendingSellerAccounts()
      }
    } catch {
      //
    } finally {
      this.isLoading = false
    }

    await this.getRegistrationChecklistItemsConfig(this.type)
  },

  methods: {
    startCase,
    ...mapActions("accounts", [
      "getPendingSellerAccounts",
      "getPendingBuyerAccounts",
      'updateSellerAccountPendingStatus',
      'updateBuyerAccountPendingStatus',
      'updateBuyerAccountDetails',
      'updateSellerAccountDetails'
    ]),

    ...mapActions('registrationChecklistItem', [
      'getRegistrationChecklistItemsConfig',
    ]),

    ...mapActions('registrationRequests', [
      'setPendingStatusForRegistrationOrAccount',
    ]),

    getOnboardingStatusBadge(status) {
      switch (status) {
        case 'Completed':
          return 'success'

        case "Pending":
        case "BankInfoSubmitted":
        case "PendingDocumentSigning":
        default:
          return "warning"
      }
    },

    formatUnmetRequirements(unmetRequirements) {
      return unmetRequirements
        .filter(r => r !== COMMON_ACCOUNT_REQUIREMENTS.ADMIN_APPROVAL_REQUIREMENT)
        .map(r => startCase(r))
        .join(', ')
    },

    async setPendingItem(itemConfig, miscellaneousItem, registrationId, accountId) {
      try {
        this.$set(this.isSetPendingItemLoading, accountId, true)

        await this.setPendingStatusForRegistrationOrAccount({
          registrationId, 
          itemConfigId: itemConfig.id, 
          accountId,  
          miscellaneousPendingItem: miscellaneousItem,
        })

        if (this.type === 'buyer') {
          this.updateBuyerAccountPendingStatus({ accountId, status: itemConfig.name == null ? miscellaneousItem : itemConfig.name })
        } else {
          this.updateSellerAccountPendingStatus({ accountId, status: itemConfig.name == null ? miscellaneousItem : itemConfig.name })
        }
      } catch {
        //
      } finally {
        this.$set(this.isSetPendingItemLoading, accountId, false)
      }
    },

    showOnboardingStatusSelect(item) {
      item.editMode = true
      item.originalStatus = item.onboardingStatus
    },

    async updateOnboardingStatus(item) {
      const typeToFunctionMap = {
        buyer: this.updateBuyerAccountDetails,
        seller: this.updateSellerAccountDetails
      }
      const updateFunction = typeToFunctionMap[this.type]
      if (updateFunction) {
        const accountId = item.id
        const onboardingStatus = item.onboardingStatus
        try {
          await updateFunction({
            accountId,
            updateObject: {
              onboardingStatus
            }
          })
          item.editMode = false
        } catch (error) {
          // Revert to original status on failure
          item.onboardingStatus = item.originalStatus
          item.editMode = false
          throw error
        }
      }
    },

    cancelUpdate(item) {
      item.onboardingStatus = item.originalStatus
      item.editMode = false
    },
  },
}
</script>
<style lang="">
  
</style>